<script>
import Ticketing from '@/services/Ticketing';
import Football from '@/services/Football';

export default {
  data() {
    return {
      showModal: false,
      tryingToSubmit: false,
      submitted: false,
      loadingOptions: false,
      form: {
        remote_match_id: '',
        match_id: '',
        match_name: '',
        match_datetime: '',
        competition_name: '',
        home_team: '',
        away_team: '',
        webview_url: '',
      },
      selectableTransactionEvents: [],
      footballMatches: [],
      filteredFootballMatches: [],
    };
  },
  watch: {
    'form.match_id'(matchId) {
      if (matchId) {
        this.onFootballMatchChange(matchId);
      }
    },
  },
  methods: {
    async openModal() {
      this.showModal = true;
      await this.loadOptions();
    },

    async loadOptions() {
      this.loadingOptions = true;
      try {
        const [matchesTicketsRes, transactionRes, footballRes] = await Promise.all([
          Ticketing.getMatchesTickets(''),
          Ticketing.getTransactionEvents(),
          Football.getMatches(),
        ]);

        const linkedRemoteIds = new Set(
          (matchesTicketsRes.data.data || []).map((m) => String(m.remote_match_id))
        );

        const transactionEvents = transactionRes.data.data || [];
        this.selectableTransactionEvents = transactionEvents.filter(
          (e) => !linkedRemoteIds.has(String(e.event_id))
        );

        const fixtures = footballRes.data.data?.fixtures || {};
        const allFixtures = [];
        const seenMatchIds = new Set();
        Object.keys(fixtures).forEach((teamKey) => {
          const list = fixtures[teamKey] || [];
          list.forEach((m) => {
            if (!seenMatchIds.has(String(m.match_id))) {
              seenMatchIds.add(String(m.match_id));
              allFixtures.push(m);
            }
          });
        });
        this.footballMatches = allFixtures;
        this.filteredFootballMatches = allFixtures;
      } catch (error) {
        const msg = error.response?.data?.error || 'Failed to load match options';
        this.failedmsg(msg);
      } finally {
        this.loadingOptions = false;
      }
    },
    
    onTransactionEventChange() {
      const event = this.selectableTransactionEvents.find(
        (e) => String(e.event_id) === String(this.form.remote_match_id)
      );
      if (!event) {
        this.filteredFootballMatches = this.footballMatches;
        return;
      }

      const eventDay = event.event_datetime
        ? String(event.event_datetime).substring(0, 10)
        : null;

      if (eventDay) {
        this.filteredFootballMatches = this.footballMatches.filter((m) => {
          const matchDay = m.date_time ? String(m.date_time).substring(0, 10) : null;
          return matchDay === eventDay;
        });
      } else {
        this.filteredFootballMatches = this.footballMatches;
      }

      if (this.filteredFootballMatches.length === 1) {
        this.onFootballMatchChange(this.filteredFootballMatches[0].match_id);
      }
    },

    onFootballMatchChange(matchId) {
      const match = this.footballMatches.find(
        (m) => String(m.match_id) === String(matchId)
      );
      if (!match) return;

      this.form.match_id = match.match_id;
      const home = match.home_team_name || match.home_team || '';
      const away = match.away_team_name || match.away_team || '';
      this.form.home_team = home;
      this.form.away_team = away;
      this.form.match_name = `${home} - ${away}`;
      this.form.match_datetime = match.date_time || this.form.match_datetime;
      this.form.competition_name = match.competition_name || '';
    },

    transactionEventLabel(event) {
      const name = event.event_name || 'Unnamed event';
      const dt = event.event_datetime || '';
      return `${name} (${dt}) — EventId: ${event.event_id}`;
    },

    footballMatchLabel(match) {
      const home = match.home_team_name || match.home_team || '';
      const away = match.away_team_name || match.away_team || '';
      return `${home} vs ${away} (${match.date_time || ''})`;
    },

    async submitForm() {
      this.submitted = true;
      if (!this.form.remote_match_id || !this.form.match_id) {
        return;
      }

      this.tryingToSubmit = true;
      try {
        await Ticketing.postMatchesTickets({
          match_id: this.form.match_id,
          remote_match_id: this.form.remote_match_id,
          match_name: this.form.match_name,
          match_datetime: this.form.match_datetime,
          competition_name: this.form.competition_name,
          home_team: this.form.home_team,
          away_team: this.form.away_team,
          webview_url: this.form.webview_url || '',
        });
        this.successmsg('Tickets match added successfully');
        this.closeModal();
        this.$emit('onRefresh');
      } catch (error) {
        const msg = error.response?.data?.error || 'Failed to add tickets match';
        this.failedmsg(msg);
      } finally {
        this.tryingToSubmit = false;
      }
    },

    closeModal() {
      this.showModal = false;
    },

    resetProps() {
      this.submitted = false;
      this.tryingToSubmit = false;
      this.form = {
        remote_match_id: '',
        match_id: '',
        match_name: '',
        match_datetime: '',
        competition_name: '',
        home_team: '',
        away_team: '',
        webview_url: '',
      };
      this.selectableTransactionEvents = [];
      this.footballMatches = [];
      this.filteredFootballMatches = [];
    },
  },
};
</script>

<template>
  <b-modal
    id="add_tickets_match"
    v-model="showModal"
    title="Add Tickets Match"
    title-class="font-18"
    centered
    @hidden="resetProps"
  >
    <div v-if="loadingOptions" class="text-center py-4">
      <b-spinner></b-spinner>
      <p class="text-muted mt-2 mb-0">Loading match options...</p>
    </div>

    <div v-else>
      <b-form-group label="Transaction event (Tymes4) *" label-for="remote_match_id">
        <b-form-select
          id="remote_match_id"
          v-model="form.remote_match_id"
          :options="[
            { value: '', text: 'Select transaction event...', disabled: true },
            ...selectableTransactionEvents.map((e) => ({
              value: e.event_id,
              text: transactionEventLabel(e),
            })),
          ]"
          :class="{ 'is-invalid': submitted && !form.remote_match_id }"
          @change="onTransactionEventChange"
        />
        <small v-if="!selectableTransactionEvents.length" class="text-muted">
          No unlinked transaction events found.
        </small>
      </b-form-group>

      <b-form-group label="Football match *" label-for="match_id">
        <b-form-select
          id="match_id"
          v-model="form.match_id"
          :options="[
            { value: '', text: 'Select football match...', disabled: true },
            ...filteredFootballMatches.map((m) => ({
              value: m.match_id,
              text: footballMatchLabel(m),
            })),
          ]"
          :class="{ 'is-invalid': submitted && !form.match_id }"
        />
      </b-form-group>

      <b-form-group label="Match name" label-for="match_name">
        <b-form-input id="match_name" v-model="form.match_name" readonly />
      </b-form-group>

      <b-form-group label="Match datetime" label-for="match_datetime">
        <b-form-input id="match_datetime" v-model="form.match_datetime" readonly />
      </b-form-group>

      <b-form-group label="Competition" label-for="competition_name">
        <b-form-input id="competition_name" v-model="form.competition_name" readonly />
      </b-form-group>

      <b-form-group label="Webview URL" label-for="webview_url">
        <b-form-input id="webview_url" v-model="form.webview_url" placeholder="Optional" />
      </b-form-group>
    </div>

    <template #modal-footer>
      <b-button variant="light" @click="closeModal">Cancel</b-button>
      <b-button
        variant="primary"
        :disabled="tryingToSubmit || loadingOptions"
        @click="submitForm"
      >
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Save
      </b-button>
    </template>
  </b-modal>
</template>
